import React, { useState, useEffect } from 'react';
import { pb, checkPocketBaseConnection } from '../lib/pocketbase';

interface EnvVariable {
  name: string;
  value: string | undefined;
  masked?: boolean;
}

const ConnectionDebug: React.FC = () => {
  const [connectionStatus, setConnectionStatus] = useState<{ success: boolean; message: string }>({ 
    success: false, 
    message: 'Checking connection...' 
  });
  const [envVariables, setEnvVariables] = useState<EnvVariable[]>([]);
  const [pbUrl, setPbUrl] = useState<string>('');

  useEffect(() => {
    // Check PocketBase connection
    const checkConnection = async () => {
      try {
        const status = await checkPocketBaseConnection();
        setConnectionStatus(status);
      } catch (error) {
        setConnectionStatus({ 
          success: false, 
          message: error instanceof Error ? error.message : 'Unknown error checking connection' 
        });
      }
    };

    // Get environment variables
    const getEnvVariables = () => {
      const variables: EnvVariable[] = [
        { name: 'VITE_POCKETBASE_URL', value: import.meta.env.VITE_POCKETBASE_URL, masked: false },
        { name: 'VITE_STRIPE_PUBLIC_KEY', value: import.meta.env.VITE_STRIPE_PUBLIC_KEY, masked: true },
        { name: 'VITE_WEBHOOK_URL', value: import.meta.env.VITE_WEBHOOK_URL, masked: false },
        { name: 'VITE_APPWRITE_ENDPOINT', value: import.meta.env.VITE_APPWRITE_ENDPOINT, masked: false },
        { name: 'VITE_APPWRITE_PROJECT_ID', value: import.meta.env.VITE_APPWRITE_PROJECT_ID, masked: true },
      ];
      setEnvVariables(variables);
    };

    // Get PocketBase URL
    setPbUrl(pb.baseUrl);

    checkConnection();
    getEnvVariables();
  }, []);

  const maskValue = (value: string | undefined): string => {
    if (!value) return 'Not set';
    if (value.length <= 8) return '********';
    return value.substring(0, 4) + '****' + value.substring(value.length - 4);
  };

  const testLogin = async () => {
    try {
      // Test with a dummy login that will fail but test the connection
      await pb.collection('v2t_users').authWithPassword('test@example.com', 'password');
    } catch (error: any) {
      // We expect this to fail with 400 (invalid credentials)
      // We're just checking if we can reach the server
      if (error.status === 400) {
        alert('Connection to PocketBase successful! (Expected authentication failure with status 400)');
      } else if (error.status === 405) {
        alert('Method Not Allowed (405) error. This is likely a CORS issue. Check your PocketBase configuration.');
      } else {
        alert(`Error: ${error.message || 'Unknown error'} (Status: ${error.status || 'unknown'})`);
      }
    }
  };

  return (
    <div className="max-w-4xl mx-auto p-6 bg-white rounded-lg shadow-lg">
      <h1 className="text-2xl font-bold mb-6">Connection Debugging</h1>
      
      <div className="mb-6">
        <h2 className="text-xl font-semibold mb-2">PocketBase Connection</h2>
        <div className="p-4 border rounded">
          <p><strong>URL:</strong> {pbUrl || 'Not set'}</p>
          <p>
            <strong>Status:</strong> 
            <span className={connectionStatus.success ? 'text-green-600' : 'text-red-600'}>
              {connectionStatus.success ? '✅ Connected' : '❌ Not Connected'}
            </span>
          </p>
          <p><strong>Message:</strong> {connectionStatus.message}</p>
          <button 
            onClick={testLogin}
            className="mt-2 px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
          >
            Test Authentication Endpoint
          </button>
        </div>
      </div>

      <div className="mb-6">
        <h2 className="text-xl font-semibold mb-2">Environment Variables</h2>
        <div className="overflow-x-auto">
          <table className="min-w-full bg-white border">
            <thead>
              <tr>
                <th className="py-2 px-4 border">Name</th>
                <th className="py-2 px-4 border">Value</th>
                <th className="py-2 px-4 border">Status</th>
              </tr>
            </thead>
            <tbody>
              {envVariables.map((variable, index) => (
                <tr key={index}>
                  <td className="py-2 px-4 border">{variable.name}</td>
                  <td className="py-2 px-4 border font-mono">
                    {variable.masked ? maskValue(variable.value) : (variable.value || 'Not set')}
                  </td>
                  <td className="py-2 px-4 border">
                    {variable.value ? 
                      <span className="text-green-600">✅</span> : 
                      <span className="text-red-600">❌</span>
                    }
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="mb-6">
        <h2 className="text-xl font-semibold mb-2">Browser Information</h2>
        <div className="p-4 border rounded">
          <p><strong>User Agent:</strong> {navigator.userAgent}</p>
          <p><strong>Current URL:</strong> {window.location.href}</p>
        </div>
      </div>

      <div className="mt-6 p-4 bg-yellow-50 border border-yellow-200 rounded">
        <h3 className="font-semibold text-yellow-800">Troubleshooting Tips</h3>
        <ul className="list-disc pl-5 mt-2 text-yellow-800">
          <li>Make sure your PocketBase server is running and accessible from your deployed app's domain</li>
          <li>Check that CORS is properly configured on your PocketBase server</li>
          <li>Verify that all environment variables are correctly set in your Cloudflare Pages dashboard</li>
          <li>Try clearing your browser cache and cookies</li>
          <li>Check browser console for additional error messages</li>
        </ul>
      </div>
    </div>
  );
};

export default ConnectionDebug;
